import { reactive, ref, computed } from "vue";
import type { MemberCreateInput, CreateMemberMutation, OperationInput } from "@/generated/graphql";
import { localStorageGetUserId } from "@/common/localStorageUtil";
import type { KanaStepA, BlockItem, ZipAddressItem } from "../types";

/**
 * 会員登録画面のフォーム状態を管理する composable。
 */
export const useMemberRegisterForm = () => {
  // 登録対象 会員
  const createMember = reactive<MemberCreateInput>({
    zip_code: "",
    address1: "",
    address2: "",
    address3: "",
    chain_code: "",
    customer_code: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    phone: "",
    notes: "",
    house_card: "",
  });

  //  会員作成 input
  const memberCreateInput = reactive<MemberCreateInput>({
    address1: "",
    address2: "",
    address3: "",
    chain_code: "",
    customer_code: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    notes: "",
    phone: "",
    shop_code: "",
    zip_code: "",
    house_card: "",
  });

  // 会員登録 レスポンス
  const createMemberMutation = ref<CreateMemberMutation | null | undefined>(null);

  // 一時退避用（仮会員ID）
  const savedTmpMemberId = ref("");

  // フォーム再生成用
  const formKey = ref(0);

  const isDisabled = ref(false);

  const house_card_display = ref(0);
  const house_card_validation = ref("");
  const house_card_required = ref(0);

  // 置換前の備考蘭
  const memberNotesBefore = ref("");
  const messageMemberNotes = ref("");

  // ログ入力
  const logInput = ref<OperationInput>({
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  });

  // 住所ダイアログ関連
  const prefListItems = ref<(string | null | undefined)[]>([]);
  const blockList = ref<
    | Array<BlockItem | null | undefined>
    | null
    | undefined
  >([]);
  const zipAddressList = ref<
    (ZipAddressItem | null | undefined)[] | null | undefined
  >();
  const sectionBlockList = ref<KanaStepA | null | undefined>(null);
  const zip_code = ref("");
  const prefSelectValue = ref("");

  const errorZipCode = ref("");
  const clickMode = ref(false);

  // 配達対象外区画リスト
  const unassignedBlockList = ref<string[]>([]);

  const btnNonMemberToggle = ref(0);
  const userErrorMessage = ref("");
  const errorPrefSelect = ref("");
  const zipCodeFromPref = ref("");
  const cityBlockList = ref<KanaStepA | null | undefined>(null);
  const originalAddress = ref("");
  const originalCityAddress = ref("");
  const originalSectionAddress = ref("");
  const fileInput = ref<HTMLInputElement | null>(null);

  // 文言データ
  const wordList = ref<Record<string, string>>({});

  // Fieldのname属性で使用される必要なラベルキー（OperatorRegister.vueと同様のパターン）
  const requiredFieldLabelKeys = [
    'lbl_member_regist',
    'lbl_name_kanji',
    'lbl_name_kana',
    'lbl_tel',
    'lbl_delivery_destination_address_detail',
    'lbl_room_num',
    'lbl_family_name_kanji',
    'lbl_first_name_kanji',
    'lbl_family_name_kana',
    'lbl_first_name_kana',
    'lbl_house_card',
    'lbl_memo_member',
    'lbl_other_block_disp_name',
    'lbl_other_block_select',
    'lbl_area_not_select',
  ] as const;

  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList.value);
  });

  return {
    createMember,
    memberCreateInput,
    createMemberMutation,
    savedTmpMemberId,
    formKey,
    isDisabled,
    house_card_display,
    house_card_validation,
    house_card_required,
    memberNotesBefore,
    messageMemberNotes,
    logInput,
    prefListItems,
    blockList,
    zipAddressList,
    sectionBlockList,
    zip_code,
    prefSelectValue,
    errorZipCode,
    clickMode,
    unassignedBlockList,
    btnNonMemberToggle,
    userErrorMessage,
    errorPrefSelect,
    zipCodeFromPref,
    cityBlockList,
    originalAddress,
    originalCityAddress,
    originalSectionAddress,
    fileInput,
    wordList,
    isWordListReady,
  };
};
